import { Heart, Instagram } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import { CONTACT } from "@/lib/site";
import useSeo from "@/lib/useSeo";
import { useT } from "@/lib/i18n";

const Testimonios = () => {
  const t = useT();
  useSeo({
    title: {
      es: "Testimonios · Voces Reales del Método TAO | Marta Montardit",
      ca: "Testimonis · Veus Reals del Mètode TAO | Marta Montardit",
    },
    description: {
      es: "Personas reales contando, en sus propias palabras, cómo el acompañamiento cuerpo-mente del Método TAO ha entrado en sus vidas durante procesos oncológicos y de fatiga crónica.",
      ca: "Persones reals explicant, amb les seves pròpies paraules, com l'acompanyament cos-ment del Mètode TAO ha entrat a les seves vides durant processos oncològics i de fatiga crònica.",
    },
    path: "/testimonios",
  });

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16" data-testid="page-testimonios">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("test.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98] max-w-3xl">
              {t("test.title1")} <span className="italic text-terracotta">{t("test.title2")}</span> {t("test.title3")}
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-2xl">
              {t("test.lead")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED VIDEO TESTIMONIAL */}
      <section className="pb-24 md:pb-32" data-testid="featured-video-testimonial">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="grid md:grid-cols-[1fr_auto] gap-12 md:gap-20 items-center">
              {/* Text side */}
              <div className="order-2 md:order-1">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-sage mb-5">
                  <Heart size={12} /> {t("test.video.eyebrow")}
                </span>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal leading-[1.05] mb-6">
                  {t("test.video.title1")} <span className="italic text-terracotta">{t("test.video.title2")}</span> {t("test.video.title3")}
                </h2>
                <p className="text-lg leading-relaxed text-charcoal/75 font-light max-w-md mb-8">
                  {t("test.video.body")}
                </p>
                <a
                  href={CONTACT.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="testimonios-instagram-link"
                  className="inline-flex items-center gap-2 text-charcoal hover:text-terracotta transition-colors text-sm border-b border-terracotta pb-1"
                >
                  <Instagram size={16} /> {t("test.video.moreVoices")} @{CONTACT.instagram}
                </a>
              </div>

              {/* Video side */}
              <div className="order-1 md:order-2">
                <div
                  className="relative rounded-2xl overflow-hidden bg-charcoal shadow-xl shadow-charcoal/15 mx-auto"
                  style={{ width: "min(100%, 340px)", aspectRatio: "9 / 16" }}
                >
                  <video
                    src="/testimonios/testimonio-alumnas.mp4"
                    poster="/testimonios/testimonio-alumnas-poster.jpg"
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                    data-testid="testimonio-video-player"
                  >
                    Tu navegador no admite vídeo HTML5.
                  </video>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SectionCTA
        title={t("cta.beNext.title")}
        body={t("cta.beNext.body")}
        primaryLabel={t("cta.beNext.button")}
        primaryTo="/sesion-gratuita"
      />
    </div>
  );
};

export default Testimonios;
